/**
 * useSearchResults (M2-B, 2026-09-15) — сохранённые результаты поиска
 * по датасету из базы истины: GET /api/v1/search/results?dataset_id=.
 *
 * - Загружается при выборе датасета (enabled по datasetId), staleTime 60s.
 * - Обновление после нового прогона — через инвалидацию из ProcessSearchPage
 *   (onSuccess старта), собственного polling нет (строки опрашивает M1-хук).
 * - 404 (prod без M2-A/M2-B бэкенда) не роняет грид: query уходит в error,
 *   UI остаётся на fallback-логике enriched_data.
 */
import { useQuery } from '@tanstack/react-query';

import api from '@/lib/api';

export interface SearchRowResult {
  row_id: string;
  row_index: number;
  item_id: string | null;
  status: string | null;
  confidence: number | null;
  attempt_count: number;
  last_run_at: string | null;
  legacy_result: boolean;
  /** Fix 2026-09-18: enriched_data engine=search_v2 без truth-записи (тестовый прогон). */
  test_run_result: boolean;
  /** Fix 2026-09-18: полный прогон отработал строку, но результат не сохранился (не truth, не тест). */
  unsaved_result: boolean;
  // TZ 2026-09-20 (test preview): явное разделение test/final для UI.
  has_final_result: boolean;
  has_test_result: boolean;
  /** none | test | final — какой результат показывать прямо сейчас. */
  current_display_result_kind: 'none' | 'test' | 'final';
  test_run_id: string | null;
  final_run_id: string | null;
  /** Сводка найденного из display-запуска (имя/SKU/бренд + число фактов). */
  summary: {
    title: string | null;
    sku: string | null;
    brand: string | null;
    facts_count: number;
  } | null;
}

export interface SearchResultsByDataset {
  dataset_id: string;
  total_rows: number;
  results: SearchRowResult[];
}

export function useSearchResults(datasetId: string | null) {
  return useQuery<SearchResultsByDataset>({
    queryKey: ['search', 'results', datasetId],
    queryFn: async () => {
      const r = await api.get<SearchResultsByDataset>('/search/results', {
        params: { dataset_id: datasetId },
      });
      return r.data;
    },
    enabled: Boolean(datasetId),
    staleTime: 60_000,
    retry: false,
  });
}
